/** @type {import('tailwindcss').Config} */
const defaultTheme = require('tailwindcss/defaultTheme')
const colors = require('tailwindcss/colors')

const isDev = process.env.NODE_ENV !== 'production' || false

const TAG_VERSION = process.env.NEXT_PUBLIC_TAG_VERSION
const PUBLIC_CDN_URL = process.env.NEXT_PUBLIC_CDN_URL || ''
const BASE_URL = isDev ? '' : `${PUBLIC_CDN_URL}/${TAG_VERSION}`

module.exports = {
    content: [
        './pages/**/*.{js,ts,jsx,tsx,mdx}',
        './components/**/*.{js,ts,jsx,tsx,mdx}',
        './app/**/*.{js,ts,jsx,tsx,mdx}',
        './app/*.{js,ts,jsx,tsx,mdx}',
        './features/**/*.{js,ts,jsx,tsx,mdx}',
    ],
    theme: {
        fontFamily: {
            'iran-sans': ['IranSans', ...defaultTheme.fontFamily.sans],
        },
        container: {
            center: true,
            padding: {
                DEFAULT: '0.5rem',
                lg: '0',
            },
        },
        screens: {
            sm: '768px',
            md: '992px',
            lg: '1200px',
        },
        extend: {
            spacing: {
                4.5: '18px',
                5.5: '22px',
                7.5: '30px',
                9.5: '38px',
                15: '60px',
                17: '68px',
                18: '72px',
                19: '76px',
                22: '88px',
                26: '104px',
                25: '100px',
                30: '120px',
                33: '132px',
                34: '136px',
                35: '140px',
                39: '156px',
            },
            fontSize: {
                xss: '.625rem',
                'title-20': ['20px', '32px'],
                'title-18': ['18px', '28px'],
                'body-16': ['16px', '24px'],
                'body-14': ['14px', '24px'],
                'caption-12': ['12px', '20px'],
                'caption-10': ['10px', '16px'],
            },
            colors: {
                gray: colors.neutral,
                brand: {
                    primary: '#F096A5',
                    secondary: '#DB2777',
                },
                // new design system
                primary: {
                    main: colors.pink[600],
                    shade: colors.pink,
                },
                secondary: {
                    main: colors.teal[400],
                    shade: colors.teal,
                },
                alert: {
                    warning: { dark: colors.orange[400], light: colors.orange[50] },
                    error: { dark: colors.rose[600], light: colors.red[50] },
                    success: { dark: colors.emerald[600], light: colors.emerald[50] },
                    info: { dark: colors.blue[500], light: colors.blue[50] },
                },
                surface: {
                    solid: {
                        0: colors.white,
                        50: colors.neutral[50],
                        100: colors.neutral[100],
                    },
                    scrim: {
                        black: '#0a0a0a56',
                        white: '#e5e5e572',
                    },
                },
                border: {
                    pink: colors.pink[200],
                    gray: {
                        light: colors.neutral[200],
                        medium: colors.neutral[300],
                    },
                },
                outline: {
                    gray: {
                        medium: colors.neutral[300],
                    },
                },
                text: {
                    white: colors.white,
                    lightGray: colors.neutral[300],
                    mediumGray: colors.neutral[400],
                    darkGray: colors.neutral[500],
                    black: colors.neutral[900],
                },
            },
            boxShadow: {
                center: '0px 2px 5px 2px rgba(33,33,33,0.07)',
                low: '0px 2px 5px 2px #2121210A',
                medium: '0px 2px 5px 2px #21212112',
                high: '0px 2px 5px 2px #2121211A',
            },
            keyframes: {
                loading: {
                    '0%': { opacity: '0.1' },
                    '100%': { opacity: '1' },
                },
                'ripple-effect': {
                    '0%': {
                        transform: 'scale(1)',
                        opacity: 1,
                    },
                    '50%': {
                        transform: 'scale(10)',
                        opacity: 0.375,
                    },
                    '100%': {
                        transform: 'scale(35)',
                        opacity: 0,
                    },
                },
                'pink-box-anition': {
                    '0%': {
                        transform: 'scale(1)',
                    },
                    '50%': {
                        transform: 'scale(1.1)',
                    },
                    '100%': {
                        transform: 'scale(1)',
                    },
                },
                'page-loading': {
                    '0%': {
                        transform: 'rotate(0)',
                    },
                    '100%': {
                        transform: 'rotate(360deg)',
                    },
                },
                scaling: {
                    '0%': {
                        transform: 'scale(0.9)',
                    },
                    '50%': {
                        transform: 'scale(1)',
                    },
                    '100%': {
                        transform: 'scale(0.9)',
                    },
                },
                'dot-flash': {
                    '0%': {
                        opacity: 0,
                    },
                    '100%': {
                        opacity: 0,
                    },
                    '50%': {
                        opacity: 1,
                    },
                },
            },
            animation: {
                ripple: '1.1s ease 1 forwards ripple-effect',
                'pink-box': '1.2s ease forwards pink-box-anition infinite',
                'page-loading': '1.2s linear page-loading infinite',
                scaling: '1.2s linear scaling infinite',
                'dot-flash': 'dot-flash 1.5s infinite',
            },
            zIndex: {
                'artin-header': '1299',
                header: '2000000000',
                'top-of-chatbot-plus1': '2000000004',
                'modal-backdrop': '2000000005',
                'category-modal': '2000000006',
                'navigation-bar': '2000000007',
                modal: '2000000008',
                toast: '2000000009',
            },
        },
    },
    plugins: [
        function ({ addBase }) {
            addBase({
                '@font-face': [
                    {
                        fontFamily: 'IranSans',
                        src: `url('${BASE_URL}/_toad/fonts/iran-sans/iran-sans-x-v.woff2') format('woff2'),
                        url('${BASE_URL}/_toad/fonts/iran-sans/iran-sans-x-v.woff') format('woff')`,
                        fontWeight: '400 700',
                        fontDisplay: 'swap',
                    },
                    {
                        fontFamily: 'IranSans',
                        src: `url('${BASE_URL}/_toad/fonts/iran-sans/woff/iran-sans-x-regular.woff') format('woff'),
                        url('${BASE_URL}/_toad/fonts/iran-sans/ttf/iran-sans-x-regular.ttf') format('truetype')`,
                        fontWeight: '400',
                        fontDisplay: 'swap',
                    },
                    {
                        fontFamily: 'IranSans',
                        src: `url('${BASE_URL}/_toad/fonts/iran-sans/woff/iran-sans-x-medium.woff') format('woff'),
                        url('${BASE_URL}/_toad/fonts/iran-sans/ttf/iran-sans-x-medium.ttf') format('truetype')`,
                        fontWeight: '500',
                        fontDisplay: 'swap',
                    },
                    {
                        fontFamily: 'IranSans',
                        src: `url('${BASE_URL}/_toad/fonts/iran-sans/woff/iran-sans-x-bold.woff') format('woff'),
                        url('${BASE_URL}/_toad/fonts/iran-sans/ttf/iran-sans-x-bold.ttf') format('truetype')`,
                        fontWeight: '700',
                        fontDisplay: 'swap',
                    },
                ],
            })
        },
    ],
}
